// Mobile port of the web Sidebar Configuration (Settings → Sidebar). Reads/
// writes the same `user_settings.sidebar_items` / `sidebar_groups` jsonb
// columns the web app's SidebarSettingsContent uses — same source of truth,
// so reordering/hiding on either platform reflects on both.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { TOOL_NAMES } from '../domain/olmps';

export interface SidebarItem {
  id: string; // web href, e.g. "/" or "/tasks" — kept as-is so both platforms share rows
  href: string;
  label: string;
  visible: boolean;
  order: number;
  groupId?: string | null;
}

export interface SidebarGroup {
  id: string;
  name: string;
  order: number;
}

// Web hrefs are task-shaped (e.g. "/tasks" for KYRS), not toolId-shaped, so
// map each web route to its mobile toolId explicitly — mirrors nav-links.ts.
// This is the primary resolver: hrefs are stable, whereas labels are stylized
// on web (e.g. "M.E.T.I.S", "SyQi AI") and won't match TOOL_NAMES verbatim.
const HREF_TO_TOOL: Record<string, string> = {
  '/tasks': 'kyrs',
  '/calendar': 'krnos',
  '/agenda': 'metis',
  '/journal': 'clio',
  '/astrs': 'astrs',
  '/nemos': 'nemos',
  '/stats': 'numa',
  '/design': 'tkne',
  '/reach': 'sofia',
  '/jnus': 'jnus',
  '/telos': 'telos',
  '/protocols': 'themis',
  '/hrmez': 'hrmez',
  '/argos': 'argos',
  '/moyri': 'moyri',
  '/syqi': 'syqi',
  '/plutus': 'plutus',
  '/hygia': 'hygia',
  '/hypnos': 'hypnos',
};

// Fallback: match a stylized label against TOOL_NAMES after stripping
// punctuation/spacing, so "M.E.T.I.S" → "metis" still resolves if a row has an
// unrecognized href.
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
const LABEL_TO_TOOL: Record<string, string> = Object.fromEntries(
  Object.entries(TOOL_NAMES).map(([id, label]) => [norm(label), id])
);

export function resolveSidebarDest(item: SidebarItem): { kind: 'home' } | { kind: 'settings' } | { kind: 'tool'; toolId: string } | null {
  if (item.href === '/') return { kind: 'home' };
  if (item.href === '/settings') return { kind: 'settings' };
  const toolId = HREF_TO_TOOL[item.href] ?? LABEL_TO_TOOL[norm(item.label)];
  return toolId ? { kind: 'tool', toolId } : null;
}

export function useSidebarConfig(userId: string | undefined) {
  const [items, setItems] = useState<SidebarItem[]>([]);
  const [groups, setGroups] = useState<SidebarGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from('user_settings')
      .select('sidebar_items, sidebar_groups')
      .eq('user_id', userId)
      .maybeSingle();
    setItems(Array.isArray(data?.sidebar_items) ? (data!.sidebar_items as SidebarItem[]) : []);
    setGroups(Array.isArray(data?.sidebar_groups) ? (data!.sidebar_groups as SidebarGroup[]) : []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const persist = useCallback(
    async (nextItems: SidebarItem[], nextGroups: SidebarGroup[]) => {
      setItems(nextItems);
      setGroups(nextGroups);
      if (!userId) return;
      await supabase
        .from('user_settings')
        .update({ sidebar_items: nextItems, sidebar_groups: nextGroups })
        .eq('user_id', userId);
    },
    [userId]
  );

  const setVisible = useCallback(
    (id: string, visible: boolean) => {
      const next = items.map((i) => (i.id === id ? { ...i, visible } : i));
      void persist(next, groups);
    },
    [items, groups, persist]
  );

  const move = useCallback(
    (id: string, direction: -1 | 1) => {
      const sorted = [...items].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((i) => i.id === id);
      const swapIdx = idx + direction;
      if (idx === -1 || swapIdx < 0 || swapIdx >= sorted.length) return;
      [sorted[idx], sorted[swapIdx]] = [sorted[swapIdx], sorted[idx]];
      sorted.forEach((item, i) => {
        item.order = i;
      });
      void persist(sorted, groups);
    },
    [items, groups, persist]
  );

  const sortedVisible = [...items].filter((i) => i.visible).sort((a, b) => a.order - b.order);
  const sortedAll = [...items].sort((a, b) => a.order - b.order);

  return { loading, items: sortedAll, visibleItems: sortedVisible, groups, setVisible, move, refresh: load };
}
